<template>
  <div ref="container" class="editor-container" />
</template>

<script>
import Vue from 'vue'

import colors from 'vuetify/lib/util/colors'

import license from './licence.json'

const ASSETS_PATH = '/photo-editor-assets'
const PHOTO_EDITOR_TRANSLATIONS_KEY = 'photo_editor'

export default {
  name: 'PhotoEditor',
  props: {
    imagePath: {
      type: String,
      default: '',
    },
    options: {
      type: Object,
    },
    showExportButton: {
      type: Boolean,
      default: true,
    },
    onExport: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      editor: null,
      image: null,
    }
  },

  watch: {
    layout() {
      this.renderUI()
    },
  },

  created() {
    this.image = new Image()
    if (!this.imagePath) return

    this.image.onload = this.renderUI.bind(this)
    this.image.src = this.imagePath
  },

  methods: {
    async renderUI() {
      await this.initReact()

      const {
        CanvasAction,
        ExportFormat,
        ImageFormat,
        PhotoEditorSDKUI,
        Tool,
        UIEvent,
      } = await import(/* webpackChunkName: "PESDK" */ 'photoeditorsdk')

      const currentLanguage = this.$i18n.i18next.language
      const translations =
        this.$i18n.i18next.getDataByLanguage(currentLanguage)?.translation?.[
          PHOTO_EDITOR_TRANSLATIONS_KEY
        ] ?? {}

      const editor = await PhotoEditorSDKUI.init({
        ...this.options,
        image: this.imagePath,
        layout: 'advanced',
        container: this.$refs.container,
        license: JSON.stringify(license),
        assetBaseUrl: ASSETS_PATH,
        mainCanvasActions: this.showExportButton ? [CanvasAction.EXPORT] : [],
        tools: [Tool.BRUSH, Tool.TRANSFORM, Tool.STICKER, Tool.TEXT],
        export: {
          image: {
            enableDownload: false,
            exportType: ExportFormat.DATA_URL,
            format: ImageFormat.JPEG,
            quality: 0.5,
          },
        },
        brush: {
          defaultSize: 0.015,
        },
        transform: {
          categories: [
            {
              identifier: 'imgly_transforms_common',
            },
          ],
          flattenCategories: true,
        },
        sticker: {
          flattenCategories: true,
          enableCustomUpload: false,
        },
        theme: 'light',
        language: currentLanguage,
        custom: {
          themes: {
            light: {
              toolbar: {
                activeBackground: colors.shades.transparent,
                activeForeground: '#365AFC',
                background: colors.grey.lighten5,
                border: 'none',
                borderColor: colors.shades.transparent,
                foreground: colors.grey.darken3,
              },
              toolControlBar: {
                background: colors.shades.white,
                borderColor: colors.shades.transparent,
                inputLabelForeground: colors.grey.darken1,
                inputLabelInactiveOpacity: '0.3',
                separatorColor: colors.shades.transparent,
                titleForeground: colors.grey.darken1,
              },
              button: {
                containedPrimaryBackground: this.$vuetify.theme.themes.light
                  .primary,
              },
              shape: {
                radiusLarge: 8,
                radiusMedium: 6,
                radiusSmall: 4,
              },
              card: {
                background: colors.grey.lighten4,
              },
            },
          },
          languages: {
            [currentLanguage]: translations,
          },
        },
      })

      this.editor = editor

      Vue.prototype.$pesdk = this.editor

      this.$pesdk.on(UIEvent.EXPORT, image => this.onExport?.(image))
    },

    async initReact() {
      if (window.React && window.ReactDom) return

      const React = await import(/* webpackChunkName: "react" */ 'react')
      const ReactDom = await import(
        /* webpackChunkName: "react-dom" */ 'react-dom'
      )

      window.React = React
      window.ReactDom = ReactDom
    },
  },
}
</script>

<style lang="scss" scoped>
.editor-container {
  width: 100%;
  height: 100%;
  z-index: 1;
  position: relative;
}
</style>

<style lang="scss">
ul[aria-label='Tool Navigation'] {
  padding-left: 0 !important;
}
</style>
